import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Search, ShieldCheck } from 'lucide-react';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { PortalHero } from '@/ui/PortalHero';

import { CAPA_CONSULTA_DE_CREDENCIADOS } from './imagensDeCapa';

type Resultado =
  | {
      encontrado: true;
      situacao: 'VIGENTE';
      tipo: string;
      titulo: string;
      versao: number;
      validadeInicio: string;
      validadeFim: string | null;
    }
  | { encontrado: false; situacao: 'NAO_CONFIRMADA'; mensagem: string };

type Estado = {
  fase: string;
  titulo: string;
  descricao: string;
  consulta: { identificador: string; rotulo: string; formato: string; exemplo: null };
  permissoes: { anonima: boolean; consultar: boolean };
  resultado: Resultado | null;
  aviso: string | null;
  privacidade: string;
  comandos: Array<{ verbo: string; habilitado: boolean }>;
  navegacao: Array<{ rotulo: string; caminho: string }>;
};

type Envelope = { contract_version: string; screen: string; event: unknown; state: Estado };
const RAIZ = '/bff/v1/j02-s03-consultar-publico';

export function ConsultaPublicaPage() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [identificador, setIdentificador] = useState('');
  const [erroTransporte, setErroTransporte] = useState<string | null>(null);

  useEffect(() => {
    void chamarApi<Envelope>(`${RAIZ}/state`, { publica: true })
      .then((resposta) => setEstado(resposta.state))
      .catch(() =>
        setErroTransporte('A consulta está temporariamente indisponível. Tente novamente.'),
      );
  }, []);

  async function executar(evento: FormEvent) {
    evento.preventDefault();
    setErroTransporte(null);
    const corpo = { identificador };
    try {
      const preparada = await chamarApi<Envelope>(`${RAIZ}/verbs/preparar_consulta`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(preparada.state);
      if (preparada.state.fase === 'INVALIDO') return;
      const validada = await chamarApi<Envelope>(`${RAIZ}/verbs/validar_identificador`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(validada.state);
      if (validada.state.fase !== 'VALIDADO') return;
      const concluida = await chamarApi<Envelope>(`${RAIZ}/verbs/consultar_regularidade`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(concluida.state);
    } catch (erro) {
      setErroTransporte(
        erro instanceof ErroApi && erro.situacao === 400
          ? 'Informe um código público com 12 caracteres válidos.'
          : 'A consulta está temporariamente indisponível. Tente novamente.',
      );
    }
  }

  if (!estado && !erroTransporte) {
    return (
      <div role="status" className="py-16 text-center text-muted-foreground">
        Carregando consulta pública…
      </div>
    );
  }

  return (
    <div>
      <PortalHero
        image={CAPA_CONSULTA_DE_CREDENCIADOS}
        title={estado?.titulo ?? 'Consultar informação pública'}
        description={estado?.descricao}
        compact
      />

      <section className="mx-auto max-w-3xl space-y-6 px-4 py-10 sm:px-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="h-5 w-5" aria-hidden="true" /> Verificar publicação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={executar}>
              <div className="space-y-2">
                <Label htmlFor="codigo-publico">
                  {estado?.consulta.rotulo ?? 'Código público da publicação'}
                </Label>
                <Input
                  id="codigo-publico"
                  value={identificador}
                  onChange={(evento) => setIdentificador(evento.target.value.toUpperCase())}
                  autoComplete="off"
                  inputMode="text"
                  maxLength={14}
                  aria-describedby="formato-codigo"
                  required
                />
                <p id="formato-codigo" className="text-sm text-muted-foreground">
                  {estado?.consulta.formato}
                </p>
              </div>
              <Button type="submit" disabled={!estado?.permissoes.consultar} className="gap-2">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Consultar situação
              </Button>
            </form>
          </CardContent>
        </Card>

        {(erroTransporte || estado?.aviso) && (
          <Alert variant="destructive">
            <AlertTitle>Não foi possível concluir</AlertTitle>
            <AlertDescription>{erroTransporte ?? estado?.aviso}</AlertDescription>
          </Alert>
        )}

        {estado?.resultado && (
          <Card aria-live="polite">
            <CardHeader>
              <CardTitle className="text-lg">Resultado da consulta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {estado.resultado.encontrado ? (
                <>
                  <p>
                    <strong>Situação:</strong> Vigente
                  </p>
                  <p>
                    <strong>Publicação:</strong> {estado.resultado.titulo}
                  </p>
                  <p>
                    <strong>Tipo e versão:</strong> {estado.resultado.tipo} · versão{' '}
                    {estado.resultado.versao}
                  </p>
                  <p>
                    <strong>Validade:</strong> desde {estado.resultado.validadeInicio}
                    {estado.resultado.validadeFim ? ` até ${estado.resultado.validadeFim}` : ''}
                  </p>
                </>
              ) : (
                <p>{estado.resultado.mensagem}</p>
              )}
            </CardContent>
          </Card>
        )}

        {estado && <p className="text-sm text-muted-foreground">{estado.privacidade}</p>}
      </section>
    </div>
  );
}
